export type PlaceOption = {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
};

export type PlaceSearchResponse = {
  places: PlaceOption[];
  usesOfflineFallback: boolean;
};

export type PlanLocationPayload = {
  plan_place_name: string;
  plan_place_address: string;
  plan_public_latitude: number | null;
  plan_public_longitude: number | null;
};

export const fallbackPlaces: PlaceOption[] = [
  {
    id: "fixture:olaf-ryes-plass",
    name: "Olaf Ryes plass",
    address: "0552 Oslo",
    latitude: 59.9235,
    longitude: 10.7582
  },
  {
    id: "fixture:sterk-loren",
    name: "Sterk Loren",
    address: "Lorenveien 44, Oslo",
    latitude: 59.9302,
    longitude: 10.7918
  },
  {
    id: "fixture:fuglen",
    name: "Fuglen",
    address: "Universitetsgata 2, Oslo",
    latitude: 59.9155,
    longitude: 10.7381
  },
  {
    id: "fixture:sofienbergparken",
    name: "Sofienbergparken",
    address: "Sofienberggata 14, Oslo",
    latitude: 59.9214,
    longitude: 10.7696
  }
];

type NominatimPlace = {
  place_id: number;
  display_name: string;
  name?: string;
  lat: string;
  lon: string;
};

export async function searchPlaces(query: string, signal?: AbortSignal): Promise<PlaceSearchResponse> {
  const trimmedQuery = query.trim();
  const fallbackMatches = matchingFallbackPlaces(trimmedQuery);

  if (trimmedQuery.length < 2) {
    return { places: [], usesOfflineFallback: false };
  }

  const url = new URL("https://nominatim.openstreetmap.org/search");
  url.searchParams.set("q", trimmedQuery);
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("addressdetails", "0");
  url.searchParams.set("limit", "6");
  url.searchParams.set("accept-language", "no,en");

  try {
    const response = await fetch(url, {
      signal,
      headers: { Accept: "application/json" }
    });

    if (!response.ok) {
      throw new Error(`Place search failed with ${response.status}`);
    }

    const places = (await response.json()) as NominatimPlace[];
    return {
      places: mergedPlaces(fallbackMatches, places.map(placeOptionFromNominatim)),
      usesOfflineFallback: false
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }

    return {
      places: fallbackMatches,
      usesOfflineFallback: true
    };
  }
}

export function planLocationPayload(placeName: string, selectedPlace: PlaceOption | null): PlanLocationPayload {
  const trimmedPlaceName = placeName.trim();

  if (!selectedPlace) {
    return {
      plan_place_name: trimmedPlaceName || "Avtalt sted",
      plan_place_address: "",
      plan_public_latitude: null,
      plan_public_longitude: null
    };
  }

  return {
    plan_place_name: selectedPlace.name,
    plan_place_address: selectedPlace.address,
    plan_public_latitude: selectedPlace.latitude,
    plan_public_longitude: selectedPlace.longitude
  };
}

function matchingFallbackPlaces(query: string): PlaceOption[] {
  const normalizedQuery = query.toLocaleLowerCase();
  if (!normalizedQuery) return [];
  return fallbackPlaces.filter(
    (place) =>
      place.name.toLocaleLowerCase().includes(normalizedQuery) ||
      place.address.toLocaleLowerCase().includes(normalizedQuery)
  );
}

function placeOptionFromNominatim(place: NominatimPlace): PlaceOption {
  const displayName = cleanDisplayName(place.display_name);
  return {
    id: `osm:${place.place_id}`,
    name: place.name?.trim() || displayName,
    address: displayName,
    latitude: Number(place.lat),
    longitude: Number(place.lon)
  };
}

function cleanDisplayName(displayName: string): string {
  return displayName
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 4)
    .join(", ");
}

function mergedPlaces(fallbackMatches: PlaceOption[], searchPlaces: PlaceOption[]): PlaceOption[] {
  const seenCoordinates = new Set<string>();
  return [...fallbackMatches, ...searchPlaces]
    .filter((place) => {
      if (!Number.isFinite(place.latitude) || !Number.isFinite(place.longitude)) {
        return false;
      }

      const key = `${place.latitude.toFixed(4)}:${place.longitude.toFixed(4)}`;
      if (seenCoordinates.has(key)) {
        return false;
      }

      seenCoordinates.add(key);
      return true;
    })
    .slice(0, 6);
}
